import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Product, PRODUCT_CATEGORIES } from '../types'
import { localDb, formatCurrency } from '../lib/db'
import { useCart } from '../contexts/CartContext'
import { useToast } from '../contexts/ToastContext'
import './Shop.css'

export default function Shop() {
    const [products, setProducts] = useState<Product[]>([])
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
    const [selectedCategory, setSelectedCategory] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const { addItem } = useCart()
    const { showToast } = useToast()

    useEffect(() => {
        // Load products from local database
        const loadProducts = () => {
            const allProducts = localDb.products.getAll()
            setProducts(allProducts)
            setFilteredProducts(allProducts)
            setIsLoading(false)
        }
        loadProducts()
    }, [])

    useEffect(() => {
        let filtered = products

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(p => p.category === selectedCategory)
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(query) ||
                p.description?.toLowerCase().includes(query)
            )
        }

        setFilteredProducts(filtered)
    }, [products, selectedCategory, searchQuery])

    const handleAddToCart = (e: React.MouseEvent, product: Product) => {
        e.preventDefault()
        e.stopPropagation()
        addItem(product)
        showToast(`${product.name} added to cart`, 'success')
    }

    const getDiscountPercentage = (mrp: number, smPrice: number) => {
        return Math.round(((mrp - smPrice) / mrp) * 100)
    }

    return (
        <div className="shop-page container">
            {/* Hero Section */}
            <section className="shop-hero">
                <div className="hero-content">
                    <h1 className="hero-title">
                        Discover <span className="text-gradient">Elegance</span>
                    </h1>
                    <p className="hero-subtitle">
                        Premium women's fashion at SM Fashion, Nalgonda
                    </p>
                </div>
                <div className="hero-decoration">
                    <div className="hero-circle hero-circle-1"></div>
                    <div className="hero-circle hero-circle-2"></div>
                </div>
            </section>

            {/* Search & Filters */}
            <section className="shop-filters">
                <div className="search-box">
                    <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="M21 21l-4.35-4.35"></path>
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="category-tabs">
                    <button
                        className={`category-tab ${selectedCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setSelectedCategory('all')}
                    >
                        All
                    </button>
                    {PRODUCT_CATEGORIES.map(category => (
                        <button
                            key={category}
                            className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>
            </section>

            {/* Products Grid */}
            <section className="products-section">
                {isLoading ? (
                    <div className="products-loading">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="product-card-skeleton">
                                <div className="skeleton" style={{ aspectRatio: '3/4' }}></div>
                                <div className="skeleton" style={{ height: '20px', marginTop: '16px' }}></div>
                                <div className="skeleton" style={{ height: '16px', marginTop: '8px', width: '60%' }}></div>
                            </div>
                        ))}
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="no-products">
                        <span className="no-products-icon">🔍</span>
                        <h3>No products found</h3>
                        <p>Try adjusting your search or filters</p>
                    </div>
                ) : (
                    <div className="products-grid">
                        {filteredProducts.map(product => (
                            <Link
                                key={product.id}
                                to={`/product/${product.id}`}
                                className="product-card"
                            >
                                <div className="product-card-image">
                                    {product.image_url ? (
                                        <img src={product.image_url} alt={product.name} />
                                    ) : (
                                        <div className="product-placeholder">👗</div>
                                    )}
                                    {getDiscountPercentage(product.mrp, product.sm_price) > 0 && (
                                        <span className="discount-badge">
                                            {getDiscountPercentage(product.mrp, product.sm_price)}% OFF
                                        </span>
                                    )}
                                    <button
                                        className="quick-add-btn"
                                        onClick={(e) => handleAddToCart(e, product)}
                                        disabled={product.stock === 0}
                                    >
                                        {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                                    </button>
                                </div>
                                <div className="product-card-content">
                                    <span className="product-category">{product.category}</span>
                                    <h3 className="product-card-title">{product.name}</h3>
                                    <div className="product-card-prices">
                                        <span className="price-sale">{formatCurrency(product.sm_price)}</span>
                                        {product.mrp > product.sm_price && (
                                            <span className="price-mrp">{formatCurrency(product.mrp)}</span>
                                        )}
                                    </div>
                                    {product.stock <= 5 && product.stock > 0 && (
                                        <span className="stock-warning">Only {product.stock} left!</span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* S-Coins Banner */}
            <section className="scoins-banner">
                <div className="scoins-banner-content">
                    <span className="scoins-banner-icon">🪙</span>
                    <div>
                        <h3>Earn S-Coins on every purchase!</h3>
                        <p>Get 10 S-Coins for every ₹100 spent. Redeem them for discounts!</p>
                    </div>
                </div>
            </section>
        </div>
    )
}
